// Archery Clash: Target Mania Client
import { sounds } from '../sound.js';

export class ArcheryGameClient {
  constructor(socket, containerEl) {
    this.socket = socket;
    this.container = containerEl;
    this.players = [];
    this.currentShooterId = null;
    this.round = 1;
    this.totalRounds = 3;
    this.wind = 0;
    this.targetY = 240;
    this.aimAngle = -0.05;
    this.bowPower = 18;
    this.arrowAnim = null;

    this.render();
    this.setupSocketEvents();
    this.setupControls();
  }

  render() {
    this.container.innerHTML = `
      <div class="archery-wrapper">
        <div class="archery-header">
          <div class="archery-meta">
            <h2>🎯 ARCHERY CLASH: TARGET MANIA</h2>
            <span class="archery-sub">Aim bow & arrow • Factor in crosswind • Hit the bullseye for 10 points!</span>
          </div>
          <div class="archery-badges">
            <span id="arch-round-badge" class="arch-badge">ROUND 1/3</span>
            <span id="arch-wind-badge" class="wind-badge">WIND: 0.0 MPH</span>
          </div>
        </div>

        <div class="archery-canvas-box">
          <canvas id="archery-canvas" width="800" height="460"></canvas>
        </div>

        <div class="archery-controls-bar">
          <div class="power-group">
            <label>🏹 BOW TENSION:</label>
            <input type="range" id="arch-power-slider" min="12" max="24" value="18" step="1" />
            <span id="arch-power-val">18</span>
          </div>
          <button id="btn-arch-release" class="btn-arch-shoot">RELEASE ARROW 🎯</button>
        </div>

        <div class="archery-scores-row" id="arch-scores-row"></div>
      </div>
    `;

    this.canvas = document.getElementById('archery-canvas');
    this.ctx = this.canvas.getContext('2d');

    const pSlider = document.getElementById('arch-power-slider');
    const pVal = document.getElementById('arch-power-val');
    pSlider?.addEventListener('input', (e) => {
      this.bowPower = parseFloat(e.target.value);
      if (pVal) pVal.innerText = `${this.bowPower}`;
    });

    document.getElementById('btn-arch-release')?.addEventListener('click', () => {
      if (this.currentShooterId === this.socket.id && !this.arrowAnim) {
        sounds.playPop();
        this.socket.emit('archery_shoot', { angle: this.aimAngle, power: this.bowPower });
      }
    });
  }

  setupControls() {
    this.mouseMoveHandler = (e) => {
      const rect = this.canvas.getBoundingClientRect();
      const mouseY = (e.clientY - rect.top) * (this.canvas.height / rect.height);
      const mouseX = (e.clientX - rect.left) * (this.canvas.width / rect.width);

      if (this.currentShooterId === this.socket.id && !this.arrowAnim) {
        this.aimAngle = Math.atan2(mouseY - 260, mouseX - 100);
        this.draw();
      }
    };

    this.canvas.addEventListener('mousemove', this.mouseMoveHandler);
  }

  setupSocketEvents() {
    this.socket.on('archery_started', (data) => {
      this.players = data.players;
      this.currentShooterId = data.currentShooterId;
      this.round = data.round;
      this.totalRounds = data.totalRounds;
      this.wind = data.wind;
      sounds.playChime();
      this.updateUI();
      this.draw();
    });

    this.socket.on('archery_targetState', (data) => {
      this.targetY = data.targetY;
      this.wind = data.wind;
      this.draw();
    });

    this.socket.on('archery_arrowShot', (data) => {
      sounds.playCard();
      this.animateArrow(data);
    });

    this.socket.on('archery_turnChange', (data) => {
      this.currentShooterId = data.currentShooterId;
      this.round = data.round;
      this.wind = data.wind;
      this.arrowAnim = null;
      this.updateUI();
      this.draw();
    });

    this.socket.on('archery_gameOver', (data) => {
      sounds.playVictory();
      alert(`🏆 MASTER ARCHER: ${data.winner?.name || 'Player'} won the Archery Clash tournament!`);
    });
  }

  updateUI() {
    const rBadge = document.getElementById('arch-round-badge');
    const wBadge = document.getElementById('arch-wind-badge');
    if (rBadge) rBadge.innerText = `ROUND ${this.round}/${this.totalRounds}`;
    if (wBadge) {
      wBadge.innerText = `WIND: ${this.wind > 0 ? `+${this.wind} 💨 ▶️` : `${this.wind} 💨 ◀️`}`;
      wBadge.style.color = Math.abs(this.wind) > 3 ? '#ef4444' : '#38bdf8';
    }
  }

  animateArrow(data) {
    const startX = 100, startY = 260;
    const targetX = 700;
    const hitY = data.hitY;
    let progress = 0;

    const animStep = () => {
      progress += 0.04;
      if (progress >= 1) {
        this.arrowAnim = null;
        sounds.playPop();
        this.draw();
        return;
      }

      const curX = startX + (targetX - startX) * progress;
      const curY = startY + (hitY - startY) * progress - Math.sin(progress * Math.PI) * 40;
      this.arrowAnim = { x: curX, y: curY };
      this.draw();
      requestAnimationFrame(animStep);
    };

    requestAnimationFrame(animStep);
  }

  draw() {
    if (!this.ctx) return;

    // Outdoor Range Background (Sky & Grass)
    this.ctx.fillStyle = '#0f172a';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    this.ctx.fillStyle = '#1e3a2f';
    this.ctx.fillRect(0, 380, this.canvas.width, 80);

    // Archery Stand on Left (X=100, Y=260)
    this.ctx.fillStyle = '#38bdf8';
    this.ctx.beginPath();
    this.ctx.arc(100, 260, 16, 0, Math.PI * 2);
    this.ctx.fill();

    // Aim Line
    if (this.currentShooterId === this.socket.id && !this.arrowAnim) {
      this.ctx.strokeStyle = 'rgba(250, 204, 21, 0.7)';
      this.ctx.setLineDash([4, 4]);
      this.ctx.lineWidth = 2;
      this.ctx.beginPath();
      this.ctx.moveTo(100, 260);
      this.ctx.lineTo(100 + Math.cos(this.aimAngle) * 120, 260 + Math.sin(this.aimAngle) * 120);
      this.ctx.stroke();
      this.ctx.setLineDash([]);
    }

    // Moving Target Board on Right (X=700, Y=this.targetY)
    const tx = 700, ty = this.targetY;
    const rings = [
      { r: 65, color: '#ffffff' },
      { r: 50, color: '#0f172a' },
      { r: 36, color: '#38bdf8' },
      { r: 22, color: '#ef4444' },
      { r: 10, color: '#fbbf24' }
    ];

    // Stand Post
    this.ctx.fillStyle = '#64748b';
    this.ctx.fillRect(tx - 4, ty, 8, 380 - ty);

    // Concentric Target Rings
    rings.forEach((ring) => {
      this.ctx.fillStyle = ring.color;
      this.ctx.beginPath();
      this.ctx.arc(tx, ty, ring.r, 0, Math.PI * 2);
      this.ctx.fill();
    });

    // Flying Arrow
    if (this.arrowAnim) {
      this.ctx.strokeStyle = '#facc15';
      this.ctx.lineWidth = 3;
      this.ctx.beginPath();
      this.ctx.moveTo(this.arrowAnim.x - 24, this.arrowAnim.y);
      this.ctx.lineTo(this.arrowAnim.x, this.arrowAnim.y);
      this.ctx.stroke();

      // Arrow Tip
      this.ctx.fillStyle = '#ef4444';
      this.ctx.beginPath();
      this.ctx.arc(this.arrowAnim.x, this.arrowAnim.y, 4, 0, Math.PI * 2);
      this.ctx.fill();
    }
  }

  destroy() {
    if (this.mouseMoveHandler) this.canvas?.removeEventListener('mousemove', this.mouseMoveHandler);
    this.socket.off('archery_started');
    this.socket.off('archery_targetState');
    this.socket.off('archery_arrowShot');
    this.socket.off('archery_turnChange');
    this.socket.off('archery_gameOver');
  }
}
